// AdvancedSettings.tsx
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Calendar, MessageSquare, CreditCard, Users, Wrench, Info, ChevronDown, X } from "lucide-react";
import { VAPI_TOOLS } from "./configs";

interface AdvancedSettingsProps {
  toggleAdvanced: () => void;
  showAdvanced: boolean;
  selectedTool: string;
  handleToolChange: (value: string) => void;
  displayError: string | null;
  customPrompt: string;
  setCustomPrompt: (value: string) => void;
}

const getToolIcon = (toolId: string) => {
  switch (toolId) {
    case 'appointment_confirmation':
      return Calendar;
    case 'recover_cancelled':
      return MessageSquare;
    case 'recover_no_show':
      return Users;
    case 'inbound_call':
      return CreditCard;
    case 'manual_upload':
      return Wrench;
    default:
      return Wrench;
  }
};

export default function AdvancedSettings({
  toggleAdvanced,
  showAdvanced,
  selectedTool,
  handleToolChange,
  displayError,
  customPrompt,
  setCustomPrompt
}: AdvancedSettingsProps) {
  const hasActiveTestScenario = !!selectedTool;
  
  return (
    <div className="shrink-0 border-t border-b border-gray-100 bg-white px-4 py-3">
      <div className={`flex items-center justify-between ${showAdvanced ? 'mb-3' : ''}`}>
        <button 
          onClick={toggleAdvanced}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline transition-all duration-200"
        >
          <span>TEST SCENARIOS</span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`} />
        </button>
        
        {hasActiveTestScenario && (
          <button
            onClick={() => handleToolChange('')}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-all duration-200"
            title="Clear test scenario"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      {showAdvanced && (
        <div className="space-y-4">
          <div>

            
            <div className="flex flex-wrap gap-1.5">
              {VAPI_TOOLS.map((tool) => {
                const IconComponent = getToolIcon(tool.id);
                const isSelected = selectedTool === tool.id;
                const isCustom = tool.id === 'manual_upload';
                
                return (
                  <button
                    key={tool.id}
                    onClick={() => handleToolChange(tool.id)}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-200
                      ${isSelected 
                        ? 'bg-blue-600 border-blue-600 text-white shadow-sm' 
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                      }
                    `}
                  >
                    <IconComponent className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                    {tool.name}
                    {isCustom && (
                      <span className={`text-xs px-1 py-0.5 rounded-full font-medium ${
                        isSelected 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        Custom
                      </span>
                    )}
                    <span title={tool.description} className="cursor-help">
                      <Info className="w-4 h-4 ml-1 text-blue-500 hover:text-blue-600 transition-colors" />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedTool === 'manual_upload' && (
            <div>
              <h3 className="text-xs font-medium text-gray-600 mb-2">
                Custom Instructions
              </h3>
              
              <Textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Describe your custom test scenario..."
                className="min-h-[60px] resize-none text-xs border-gray-200"
              />
              
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs">
                  {customPrompt.length > 0 && (
                    <span className={`flex items-center gap-1 ${customPrompt.length > 20 ? 'text-green-600' : 'text-gray-500'}`}>
                      {customPrompt.length > 20 ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          Ready
                        </>
                      ) : (
                        'Add more details...'
                      )}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {customPrompt.length}/500
                </div>
              </div>
            </div>
          )}

          {displayError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-red-700 font-semibold">{displayError}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}